"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Clock3, LoaderCircle } from "lucide-react";
import type { Service } from "@/lib/content-types";
import { BookingDrawer } from "@/components/booking-drawer";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type AvailabilityResponse = {
  days: Record<string, string[]>;
};

const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const weekdayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const pad = (value: number) => String(value).padStart(2, "0");
const dateKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export function BookingCalendar() {
  const [liveServices, setLiveServices] = useState<Service[]>([]);
  const [serviceId, setServiceId] = useState("");
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [availability, setAvailability] = useState<Record<string, string[]>>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const service = liveServices.find((item) => item.id === serviceId) ?? liveServices[0];

  useEffect(() => {
    void fetch("/api/services", { cache: "no-store" }).then((response) => response.ok ? response.json() : null).then((data) => {
      if (!Array.isArray(data?.services) || !data.services.length) return;
      setLiveServices(data.services);
      const requestedServiceId = new URLSearchParams(window.location.search).get("serviceId");
      setServiceId((current) => {
        if (data.services.some((item: { id: string }) => item.id === current)) return current;
        if (requestedServiceId && data.services.some((item: { id: string }) => item.id === requestedServiceId)) return requestedServiceId;
        return data.services[0].id;
      });
    }).catch(() => undefined);
  }, []);

  const calendarDays = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const mondayIndex = (first.getDay() + 6) % 7;
    const cells: Array<Date | null> = Array.from({ length: mondayIndex }, () => null);
    for (let day = 1; day <= last.getDate(); day += 1) cells.push(new Date(month.getFullYear(), month.getMonth(), day));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  useEffect(() => {
    const controller = new AbortController();
    const from = dateKey(new Date(month.getFullYear(), month.getMonth(), 1));
    const to = dateKey(new Date(month.getFullYear(), month.getMonth() + 1, 0));
    if (!serviceId) return () => controller.abort();
    fetch(`/api/availability?serviceId=${encodeURIComponent(serviceId)}&from=${from}&to=${to}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Impossible de charger les disponibilités.");
        return response.json() as Promise<AvailabilityResponse>;
      })
      .then((data) => setAvailability(data.days))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [month, serviceId]);

  const slots = selectedDate ? availability[selectedDate] || [] : [];

  function changeMonth(offset: number) {
    setLoading(true);
    setError("");
    setSelectedDate(null);
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  }

  return (
    <div className="booking-shell">
      {!service ? <div className="calendar-status error">Aucune prestation n’est actuellement disponible.</div> : <><div className="booking-controls">
        <label htmlFor="service">Prestation</label>
        <Select value={serviceId} onValueChange={(value) => {
          setLoading(true);
          setError("");
          setSelectedDate(null);
          setServiceId(value);
        }}>
          <SelectTrigger id="service" className="booking-service-select"><SelectValue placeholder="Choisir une prestation" /></SelectTrigger>
          <SelectContent>
            <SelectGroup>{liveServices.map((item) => <SelectItem value={item.id} key={item.id}>{item.category} — {item.name} ({item.durationMinutes} min)</SelectItem>)}</SelectGroup>
          </SelectContent>
        </Select>
        <div className="selected-service-summary"><Clock3 size={17} /><span>Durée réservée : <strong>{service.durationMinutes} minutes</strong></span></div>
      </div>

      <div className="calendar-layout">
        <div className="calendar-panel">
          <div className="calendar-toolbar">
            <button type="button" onClick={() => changeMonth(-1)} aria-label="Mois précédent"><ChevronLeft /></button>
            <h3>{monthNames[month.getMonth()]} {month.getFullYear()}</h3>
            <button type="button" onClick={() => changeMonth(1)} aria-label="Mois suivant"><ChevronRight /></button>
          </div>
          <div className="calendar-weekdays">{weekdayNames.map((day) => <span key={day}>{day}</span>)}</div>
          <div className="calendar-grid">
            {calendarDays.map((day, index) => {
              if (!day) return <span className="calendar-empty" key={`empty-${index}`} />;
              const key = dateKey(day);
              const available = (availability[key]?.length || 0) > 0;
              const selected = selectedDate === key;
              return (
                <button type="button" key={key} className={`calendar-day ${available ? "available" : "unavailable"} ${selected ? "selected" : ""}`} disabled={!available || loading} onClick={() => setSelectedDate(key)}>
                  <span>{day.getDate()}</span>{available && <i>{availability[key].length}</i>}
                </button>
              );
            })}
          </div>
          {loading && <div className="calendar-status"><LoaderCircle className="spin" size={18} /> Chargement des créneaux…</div>}
          {error && <div className="calendar-status error">{error}</div>}
          {!loading && !error && <div className="calendar-legend"><span><i className="legend-dot available" /> Disponible</span><span><i className="legend-dot unavailable" /> Indisponible</span></div>}
        </div>

        <aside className="slots-panel">
          <p className="slots-title">Créneaux disponibles</p>
          {!selectedDate ? <div className="slots-empty">Choisissez une date disponible dans le calendrier.</div> : (
            <>
              <h3>{new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${selectedDate}T12:00:00`))}</h3>
              <div className="slots-grid">
                {slots.map((slot) => (
                  <button key={slot} type="button" onClick={() => setSelectedSlot(`${selectedDate} ${slot}:00`)}>{slot}</button>
                ))}
              </div>
            </>
          )}
        </aside>
      </div>

      <BookingDrawer service={service} startAt={selectedSlot} onClose={() => setSelectedSlot(null)} onBooked={() => {
        setSelectedSlot(null);
        setSelectedDate(null);
        setMonth((current) => new Date(current));
      }} /></>}
    </div>
  );
}
